import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { object, string, array } from 'yup';
import _ from 'lodash';
import { useFormikContext, ErrorMessage } from 'formik';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import ListHttpTable from '~/commons/components/ListHttpTable.jsx';
import FormResource from './FormResource.jsx';

import { subjects } from '../../../../../../components/quiz/config/subjects';
import { getLang } from '../../../commons/helpers';

const schema = object().shape({
  subject: string().required(),
  articles: array().of(string()).min(1, 'At least 1 articles are required'),
});

const defaults = {
  subject: '',
  isSenior: false,
  articles: [],
};

// Article selection component
const ArticleSelector = () => {
  const {
    values, setFieldValue, errors, touched,
  } = useFormikContext();
  const [search, setSearch] = useState('');
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const toggleArticle = (articleId) => {
    const currentArticles = values.articles || [];
    const isSelected = currentArticles.includes(articleId);

    if (isSelected) {
      setFieldValue(
        'articles',
        currentArticles.filter((id) => id !== articleId),
      );
    } else {
      setFieldValue('articles', [...currentArticles, articleId]);
    }
  };

  const hasError = touched.articles && errors.articles;

  const CheckboxCell = ({ row }) => (
    <input
      type="checkbox"
      checked={(values.articles || []).includes(row._id)}
      onChange={() => toggleArticle(row._id)}
    />
  );
  CheckboxCell.displayName = 'CheckboxCell';

  return (
    <div className="mt-4">
      <h5 className={hasError ? 'text-danger' : ''}>
        {t('Select Articles')} ({(values.articles || []).length}/∞) *
      </h5>
      {hasError && (
        <div className="text-danger small mb-2">{errors.articles}</div>
      )}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder={`${t('Search articles')}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ListHttpTable
        api={{ url: '/api/quiz/articles' }}
        sort={{ title: 1 }}
        filter={{ search }}
        cols={[
          {
            key: 'selected',
            children: '',
            getter: function renderCheckbox(value, row) {
              return <CheckboxCell row={row} />;
            },
          },
          { key: 'refCode', children: t('Code'), sortable: true },
          { key: 'title', children: t('Title'), sortable: true },
          {
            key: 'subjects',
            children: t('subjects'),
            getter: (v) => {
              const subjectArray = _.map(v, (subjectKey) => {
                const subjectObj = subjects[subjectKey];
                if (subjectObj) {
                  return getLang(subjectObj, language);
                }
                return subjectKey;
              });
              return subjectArray.join(', ') || '';
            },
          },
        ]}
        onRowClick={(row) => toggleArticle(row._id)}
      />
    </div>
  );
};
ArticleSelector.displayName = 'ArticleSelector';

const FormTraining = ({ training, onSubmit }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <FormResource
      {...{
        defaults,
        schema,
        stored: training,
        api: '/api/quiz/trainings',
        onSubmit,
      }}
    >
      <div className="row">
        <div className="col-12">
          <FormGroup name="subject" label={t('subject')} as="select">
            <option value=""> - </option>

            {_.map(subjects, (s, key) => (
              <option key={key} value={key}>
                {getLang(s, language)}
              </option>
            ))}
          </FormGroup>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <FormCheckGroup
            name="isSenior"
            label={t('isSenior')}
            type="checkbox"
            asSwitch
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <ArticleSelector />
        </div>
      </div>
    </FormResource>
  );
};

export default FormTraining;
